import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';

import MomentUtils from '@date-io/moment';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers/MuiPickersUtilsProvider';
import clsx from 'clsx';

import { CalendarIconMain } from '../../../assets';
import { CalendarPicker, CalendarRangePicker } from '../../pickers';
import { Popper } from '../../popper/Popper';
import { PopperHandle } from '../../popper/Popper.types';
import { CalendarAdornmentProps } from './CalendarAdornment.props';
import { useStyles } from './CalendarAdornment.styles';
import { useDatePicker } from './hooks/useDatePicker';
import { useRangeDatePicker } from './hooks/useRangeDatePicker';

/**
 * @component CalendarAdornment
 * @private
 * @description Internal component that provides calendar functionality for date inputs
 */
export const CalendarAdornment = memo(
  ({
    open: controlledOpen,
    value,
    type = 'date',
    dateFormat,
    minDate,
    maxDate,
    popperProps,
    disabled,
    className,
    onChange,
    onPopperOpenChange,
  }: CalendarAdornmentProps) => {
    const styles = useStyles();

    const popperRef = useRef<PopperHandle | null>(null);

    const [disableClickAway, setDisableClickAway] = useState<boolean>(false);

    const { dateValue, handleDateChange } = useDatePicker({
      value,
      type,
      dateFormat,
      disabled,
      onChange,
    });

    const { startRangeDate, endRangeDate, handleRangeDateChange } = useRangeDatePicker({
      value,
      type,
      dateFormat,
      onChange,
    });

    const handleValidateDate = useCallback(() => {
      if (type === 'date' && value) {
        if (minDate && dateValue.isBefore(minDate, 'day')) {
          handleDateChange(minDate.toDate());
        }

        if (maxDate && dateValue.isAfter(maxDate, 'day')) {
          handleDateChange(maxDate.toDate());
        }
      }
    }, [type, value, dateValue, minDate, maxDate, handleDateChange]);

    // Handle toggle click - remove focus to hide :focus-within styles when closing
    const handleToggleClick = useCallback(() => {
      if (popperRef.current?.isOpen()) {
        // Calendar is being closed via toggle click
        // Remove focus to prevent :focus-within from keeping the border
        requestAnimationFrame(() => {
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        });
      }
    }, []);

    useLayoutEffect(() => {
      if (typeof controlledOpen !== 'boolean') {
        return;
      }

      if (controlledOpen) {
        setDisableClickAway(true);
        popperRef.current?.open();
      } else {
        setDisableClickAway(false);
      }
    }, [controlledOpen]);

    return (
      <InputAdornment position="end" className={className}>
        <Popper
          ref={popperRef}
          toggle={
            <IconButton size="small" disabled={disabled}>
              <CalendarIconMain />
            </IconButton>
          }
          disabled={disabled}
          disableClickAway={disableClickAway}
          toggleProps={{
            onClick: handleToggleClick,
            onTouchEnd: handleToggleClick,
          }}
          popperProps={{
            ...popperProps,
            className: clsx(styles.popper, popperProps?.className),
          }}
          onOpenChange={onPopperOpenChange}
          onClose={handleValidateDate}
        >
          <MuiPickersUtilsProvider utils={MomentUtils}>
            {type === 'date' && (
              <CalendarPicker
                date={dateValue}
                minDate={minDate}
                maxDate={maxDate}
                onChange={date => handleDateChange(date?.toDate(), () => popperRef?.current?.close())}
              />
            )}

            {type === 'date-range' && (
              <CalendarRangePicker
                isRange
                startDate={startRangeDate}
                endDate={endRangeDate}
                minDate={minDate?.toDate()}
                maxDate={maxDate?.toDate()}
                onChange={dates => handleRangeDateChange(dates, () => popperRef?.current?.close())}
              />
            )}
          </MuiPickersUtilsProvider>
        </Popper>
      </InputAdornment>
    );
  },
);

CalendarAdornment.displayName = 'CalendarAdornment';
